import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../services/api';

const NAV = [
  { id: 'overview', icon: '📊', label: 'Overview' },
  { id: 'tenants',  icon: '🏢', label: 'Tenants' },
  { id: 'users',    icon: '👥', label: 'All Users' },
  { id: 'chats',    icon: '💬', label: 'All Chats' },
];

export default function SuperApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive]         = useState('overview');
  const [isMobile, setIsMobile]     = useState(window.innerWidth <= 768);
  const [stats, setStats]           = useState(null);
  const [tenants, setTenants]       = useState([]);
  const [users, setUsers]           = useState([]);
  const [convs, setConvs]           = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages]     = useState([]);
  const [tenantFilter, setTenantFilter] = useState('');
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { loadStats(); loadTenants(); }, []);

  useEffect(() => {
    if (active === 'users') loadUsers();
    if (active === 'chats') loadConvs();
  }, [active, tenantFilter]);

  const loadStats   = () => API.get('/super/stats').then(r => setStats(r.data)).catch(console.error);
  const loadTenants = () => API.get('/super/tenants').then(r => setTenants(r.data)).catch(console.error);
  const loadUsers   = () => API.get(`/super/users${tenantFilter ? `?tenantId=${tenantFilter}` : ''}`).then(r => setUsers(r.data)).catch(console.error);
  const loadConvs   = () => API.get(`/super/conversations${tenantFilter ? `?tenantId=${tenantFilter}` : ''}`).then(r => setConvs(r.data)).catch(console.error);

  const loadMessages = (userId) => {
    setLoading(true);
    API.get(`/super/messages/${userId}`)
      .then(r => setMessages(r.data.messages || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const blockUser = async (userId) => {
    if (!window.confirm('Block this user across the platform?')) return;
    await API.put(`/super/users/${userId}/block`);
    loadUsers();
  };

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  const renderContent = () => {
    if (active === 'overview') return (
      <div style={s.content}>
        <div style={s.pageHead}>
          <div style={s.pageTag}>PLATFORM OVERVIEW</div>
          <h1 style={s.pageTitle}>Global Stats</h1>
        </div>
        {stats ? (
          <div style={s.statsGrid}>
            {[
              { label: 'Total Tenants',   value: stats.totalTenants,        icon: '🏢' },
              { label: 'Total Users',     value: stats.totalUsers,          icon: '👥' },
              { label: 'Total Admins',    value: stats.totalAdmins,         icon: '👤' },
              { label: 'Total Messages',  value: stats.totalMessages,       icon: '💬' },
              { label: 'Conversations',   value: stats.totalConversations,  icon: '🗨️' },
            ].map(st => (
              <div key={st.label} style={s.statCard}>
                <div style={s.statIcon}>{st.icon}</div>
                <div style={s.statValue}>{st.value?.toLocaleString()}</div>
                <div style={s.statLabel}>{st.label}</div>
              </div>
            ))}
          </div>
        ) : <div style={s.loading}>Loading stats...</div>}

        <div style={{ ...s.pageTag, marginTop: '32px', marginBottom: '14px' }}>TENANTS</div>
        <div style={s.tenantList}>
          {tenants.map(t => (
            <div key={t.tenantId} style={s.tenantCard}>
              <div style={s.tenantId}>🏢 {t.tenantId}</div>
              <div style={s.tenantMeta}>
                <span style={s.metaBadge}>{t.userCount} users</span>
                <span style={s.metaBadge}>{t.adminCount} admins</span>
                <span style={s.metaBadge}>{t.msgCount} messages</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    if (active === 'tenants') return (
      <div style={s.content}>
        <div style={s.pageHead}>
          <div style={s.pageTag}>ALL TENANTS</div>
          <h1 style={s.pageTitle}>Tenant Management</h1>
        </div>
        {tenants.map(t => (
          <div key={t.tenantId} style={{ ...s.tenantCard, marginBottom: '12px' }}>
            <div style={s.tenantId}>🏢 {t.tenantId}</div>
            <div style={s.tenantMeta}>
              <span style={s.metaBadge}>{t.userCount} users</span>
              <span style={s.metaBadge}>{t.adminCount} admins</span>
              <span style={s.metaBadge}>{t.msgCount} messages</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
              <button style={s.filterBtn} onClick={() => { setTenantFilter(t.tenantId); setActive('users'); }}>View Users</button>
              <button style={s.filterBtn} onClick={() => { setTenantFilter(t.tenantId); setActive('chats'); }}>View Chats</button>
            </div>
          </div>
        ))}
      </div>
    );

    if (active === 'users') return (
      <div style={s.content}>
        <div style={s.pageHead}>
          <div style={s.pageTag}>ALL USERS</div>
          <h1 style={s.pageTitle}>User Management</h1>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <select style={s.select} value={tenantFilter} onChange={e => setTenantFilter(e.target.value)}>
            <option value="">All Tenants</option>
            {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.tenantId}</option>)}
          </select>
        </div>
        <div style={s.userList}>
          {users.map(u => (
            <div key={u._id} style={s.userRow}>
              <div style={s.userAvatar}>{(u.name || 'U').slice(0, 2).toUpperCase()}</div>
              <div style={s.userInfo}>
                <div style={s.userName}>{u.name}</div>
                <div style={s.userMeta}>{u.email} · {u.tenantId}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                {u.isBlocked && <span style={s.blockedBadge}>Blocked</span>}
                {u.isVip     && <span style={s.vipBadge}>⭐ VIP</span>}
                {!u.isBlocked && <button style={s.dangerBtn} onClick={() => blockUser(u._id)}>Block</button>}
              </div>
            </div>
          ))}
          {users.length === 0 && <div style={s.empty}>No users found</div>}
        </div>
      </div>
    );

    if (active === 'chats') return (
      <div style={s.content}>
        <div style={s.pageHead}>
          <div style={s.pageTag}>ALL CONVERSATIONS</div>
          <h1 style={s.pageTitle}>Chat Monitor</h1>
        </div>
        <div style={{ marginBottom: '14px' }}>
          <select style={s.select} value={tenantFilter} onChange={e => setTenantFilter(e.target.value)}>
            <option value="">All Tenants</option>
            {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.tenantId}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 280px)', minHeight: '300px' }}>
          <div style={s.convList}>
            {convs.map(conv => (
              <div key={conv._id}
                style={{ ...s.convItem, ...(selectedConv?._id === conv._id ? s.convItemActive : {}) }}
                onClick={() => { setSelectedConv(conv); loadMessages(conv.userId?._id); }}>
                <div style={s.convAvatar}>{(conv.userId?.name || 'U').slice(0, 2).toUpperCase()}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={s.convName}>{conv.userId?.name}</div>
                  <div style={s.convTenant}>{conv.userId?.tenantId}</div>
                  <div style={s.convPreview}>{conv.lastMessage?.slice(0, 40)}</div>
                </div>
              </div>
            ))}
            {convs.length === 0 && <div style={s.empty}>No conversations</div>}
          </div>
          <div style={s.msgView}>
            {!selectedConv ? (
              <div style={s.msgEmpty}>Select a conversation to view messages</div>
            ) : loading ? (
              <div style={s.msgEmpty}>Loading messages...</div>
            ) : (
              <div style={{ overflowY: 'auto', height: '100%', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {messages.map(msg => (
                  <div key={msg._id} style={{ alignSelf: msg.senderRole === 'user' ? 'flex-start' : 'flex-end', maxWidth: '70%' }}>
                    <div style={{ background: msg.senderRole === 'user' ? '#0d1420' : 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.1)', borderRadius: '12px', padding: '10px 14px' }}>
                      <div style={{ color: '#e2e8f0', fontSize: '13px' }}>
                        {msg.isE2EEncrypted ? '🔒 [Encrypted]' : (msg.content || `[${msg.type}]`)}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,.25)', fontSize: '10px', marginTop: '4px' }}>
                        {msg.senderRole} · {new Date(msg.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && <div style={s.msgEmpty}>No messages yet</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={s.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@700&family=Exo+2:wght@400;600;700;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;}
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:rgba(255,50,50,.2);border-radius:4px}
        .snav:hover{background:rgba(255,50,50,.06)!important;color:rgba(255,255,255,.8)!important}
      `}</style>

      {!isMobile && (
        <div style={s.sidebar}>
          <div style={s.brand}>
            <img src="/logo.jpg" alt="MegaFishHub" style={{ height: '38px', width: 'auto', objectFit: 'contain' }} />
            <span style={s.superBadge}>SUPER</span>
          </div>
          <div style={s.adminCard}>
            <div style={s.adminAvatar}>SA</div>
            <div>
              <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600' }}>{user?.name}</div>
              <div style={{ color: 'rgba(255,50,50,.8)', fontSize: '10px', marginTop: '2px' }}>⭐ Superadmin</div>
            </div>
          </div>
          <nav style={s.nav}>
            {NAV.map(item => (
              <button key={item.id} className="snav" onClick={() => setActive(item.id)}
                style={{ ...s.navItem, ...(active === item.id ? s.navActive : {}) }}>
                <span style={{ fontSize: '17px', width: '22px' }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
              </button>
            ))}
          </nav>
          <button style={s.logoutBtn} onClick={handleLogout}>⏻ &nbsp;Logout</button>
        </div>
      )}

      {isMobile && (
        <div style={s.mobileTop}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <img src="/logo.jpg" alt="MegaFishHub" style={{ height: '38px', width: 'auto', objectFit: 'contain' }} />
            <span style={s.superBadge}>SUPER</span>
          </div>
          <button style={s.mobileLogout} onClick={handleLogout}>⏻</button>
        </div>
      )}

      <div style={{ ...s.main, ...(isMobile ? s.mainMobile : {}) }}>
        {renderContent()}
      </div>

      {isMobile && (
        <div style={s.bottomNav}>
          {NAV.map(item => (
            <button key={item.id} onClick={() => setActive(item.id)}
              style={{ ...s.bottomTab, ...(active === item.id ? s.bottomTabActive : {}) }}>
              <span style={{ fontSize: '20px' }}>{item.icon}</span>
              <span style={{ fontSize: '9px', marginTop: '2px' }}>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  root: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#080c14', fontFamily: "'Exo 2',sans-serif" },
  sidebar: { width: '210px', minWidth: '210px', background: '#0d1420', borderRight: '1px solid rgba(255,50,50,.15)', display: 'flex', flexDirection: 'column', height: '100vh', flexShrink: 0 },
  brand: { display: 'flex', alignItems: 'center', gap: '8px', padding: '18px 14px', borderBottom: '1px solid rgba(255,50,50,.1)', fontSize: '18px' },
  brandText: { fontFamily: "'Rajdhani',sans-serif", fontSize: '19px', fontWeight: '700', color: '#f5a623', letterSpacing: '5px' },
  superBadge: { background: 'rgba(255,50,50,.15)', border: '1px solid rgba(255,50,50,.3)', color: '#ff6b6b', fontSize: '9px', fontWeight: '900', borderRadius: '4px', padding: '2px 5px', letterSpacing: '1px' },
  adminCard: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', margin: '10px', borderRadius: '12px', background: 'rgba(255,50,50,.05)', border: '1px solid rgba(255,50,50,.1)' },
  adminAvatar: { width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,50,50,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff6b6b', fontSize: '12px', fontWeight: '900', flexShrink: 0 },
  nav: { flex: 1, padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navItem: { display: 'flex', alignItems: 'center', gap: '9px', padding: '11px 12px', borderRadius: '11px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,.4)', cursor: 'pointer', textAlign: 'left', width: '100%', fontSize: '13px', fontWeight: '600', fontFamily: "'Exo 2',sans-serif", transition: 'all .15s' },
  navActive: { background: 'rgba(255,50,50,.08)', color: '#ff6b6b', borderLeft: '3px solid #ff6b6b' },
  logoutBtn: { margin: '12px', background: 'rgba(255,50,50,.07)', border: '1px solid rgba(255,50,50,.15)', borderRadius: '10px', color: '#ff6b6b', padding: '10px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2',sans-serif" },
  mobileTop: { position: 'fixed', top: 0, left: 0, right: 0, height: '52px', background: '#0d1420', borderBottom: '1px solid rgba(255,50,50,.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', zIndex: 100 },
  mobileLogout: { background: 'rgba(255,50,50,.08)', border: '1px solid rgba(255,50,50,.15)', borderRadius: '8px', color: '#ff6b6b', cursor: 'pointer', padding: '5px 9px', fontSize: '15px' },
  main: { flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' },
  mainMobile: { paddingTop: '52px', paddingBottom: '60px' },
  content: { padding: '28px 24px', maxWidth: '1200px', margin: '0 auto', width: '100%' },
  pageHead: { marginBottom: '24px' },
  pageTag: { color: 'rgba(255,50,50,.7)', fontSize: '10px', fontWeight: '900', letterSpacing: '4px', marginBottom: '6px' },
  pageTitle: { fontFamily: "'Rajdhani',sans-serif", fontSize: '32px', fontWeight: '700', color: '#e2e8f0' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: '14px', marginBottom: '12px' },
  statCard: { background: '#0d1420', borderRadius: '14px', border: '1px solid rgba(255,50,50,.1)', padding: '20px', textAlign: 'center' },
  statIcon: { fontSize: '28px', marginBottom: '8px' },
  statValue: { fontFamily: "'Rajdhani',sans-serif", fontSize: '28px', fontWeight: '700', color: '#ff6b6b' },
  statLabel: { color: 'rgba(255,255,255,.3)', fontSize: '11px', marginTop: '4px', letterSpacing: '1px' },
  tenantList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  tenantCard: { background: '#0d1420', borderRadius: '14px', border: '1px solid rgba(255,50,50,.1)', padding: '18px' },
  tenantId: { color: '#e2e8f0', fontSize: '16px', fontWeight: '700', marginBottom: '8px', fontFamily: "'Rajdhani',sans-serif" },
  tenantMeta: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  metaBadge: { background: 'rgba(255,50,50,.08)', border: '1px solid rgba(255,50,50,.15)', color: '#ff9a9a', borderRadius: '6px', padding: '3px 9px', fontSize: '11px' },
  filterBtn: { background: 'rgba(245,166,35,.06)', border: '1px solid rgba(245,166,35,.2)', borderRadius: '8px', color: '#f5a623', padding: '7px 14px', cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2',sans-serif" },
  select: { background: '#0d1420', border: '1px solid rgba(255,50,50,.2)', borderRadius: '10px', padding: '9px 14px', color: '#e2e8f0', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", outline: 'none' },
  userList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  userRow: { display: 'flex', alignItems: 'center', gap: '12px', background: '#0d1420', borderRadius: '12px', border: '1px solid rgba(255,255,255,.05)', padding: '12px 16px' },
  userAvatar: { width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(245,166,35,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5a623', fontSize: '13px', fontWeight: '900', flexShrink: 0 },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { color: '#e2e8f0', fontSize: '14px', fontWeight: '600' },
  userMeta: { color: 'rgba(255,255,255,.3)', fontSize: '11px' },
  blockedBadge: { background: 'rgba(255,50,50,.1)', border: '1px solid rgba(255,50,50,.2)', color: '#ff6b6b', borderRadius: '6px', padding: '2px 8px', fontSize: '11px' },
  vipBadge: { background: 'rgba(255,215,0,.1)', border: '1px solid rgba(255,215,0,.2)', color: '#ffd700', borderRadius: '6px', padding: '2px 8px', fontSize: '11px' },
  dangerBtn: { background: 'rgba(255,50,50,.08)', border: '1px solid rgba(255,50,50,.2)', borderRadius: '7px', color: '#ff6b6b', padding: '5px 12px', cursor: 'pointer', fontSize: '11px', fontFamily: "'Exo 2',sans-serif" },
  convList: { width: '260px', minWidth: '260px', overflowY: 'auto', background: '#0d1420', borderRadius: '14px', border: '1px solid rgba(255,255,255,.05)' },
  convItem: { display: 'flex', gap: '10px', padding: '12px 14px', cursor: 'pointer', borderLeft: '3px solid transparent', transition: 'all .15s' },
  convItemActive: { background: 'rgba(255,50,50,.06)', borderLeft: '3px solid #ff6b6b' },
  convAvatar: { width: '36px', height: '36px', borderRadius: '9px', background: 'rgba(245,166,35,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5a623', fontSize: '12px', fontWeight: '900', flexShrink: 0 },
  convName: { color: '#e2e8f0', fontSize: '13px', fontWeight: '600' },
  convTenant: { color: 'rgba(255,50,50,.6)', fontSize: '10px' },
  convPreview: { color: 'rgba(255,255,255,.3)', fontSize: '11px', marginTop: '2px' },
  msgView: { flex: 1, background: '#0d1420', borderRadius: '14px', border: '1px solid rgba(255,255,255,.05)', overflow: 'hidden' },
  msgEmpty: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,.25)', fontSize: '14px' },
  loading: { color: 'rgba(255,255,255,.25)', fontSize: '14px', padding: '40px', textAlign: 'center' },
  empty: { color: 'rgba(255,255,255,.2)', fontSize: '13px', padding: '30px', textAlign: 'center' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, height: '60px', background: '#0d1420', borderTop: '1px solid rgba(255,50,50,.15)', display: 'flex', zIndex: 100 },
  bottomTab: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontFamily: "'Exo 2',sans-serif", transition: 'all .15s' },
  bottomTabActive: { color: '#ff6b6b', background: 'rgba(255,50,50,.05)' },
};
